import React, { useRef, useEffect } from 'react'
import {
  IoPlaySharp,
  IoPauseSharp,
  IoStopSharp,
  IoExpandSharp,
  IoClose,
  IoRefreshSharp,
} from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/modules/rootReducer'
import IconButton from '../IconButton'
import PauseTimer from '../PauseTimer'
import { useTimer } from '../../../hooks/useTimer'
import { useTimerCardTour } from '../hooks/useTimerCardTour'
import useMediaQuery from '../../../hooks/use-media-query'
import {
  CardContainer,
  CardHeader,
  DisciplineLabel,
  CardBody,
  TimeDisplay,
  TimeSubtext,
  ProgressSection,
  ProgressLabel,
  ProgressBar,
  ProgressFill,
  ProgressValue,
  AddedTimeBadge,
  CardFooter,
  ButtonContainer,
  MobileLayoutContainer,
  MobileButtonGroup,
  MobileTimeDisplay,
} from './styles'

interface ITimerCardProps {
  onStopClick: () => void
  onCloseClick: () => void
}

/**
 * Card flutuante do cronômetro com design Bauhaus
 * - Borda com gradiente animado que segue o mouse
 * - Exibe disciplina, tempo restante e barra de progresso
 * - Botões de controle com tooltips
 */
const TimerCard: React.FC<ITimerCardProps> = ({ onStopClick, onCloseClick }) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const { isDarkMode } = useSelector((state: RootState) => state.theme)
  const isMobile = useMediaQuery('(max-width: 768px)')

  // Hook para gerenciar o mini-tour do TimerCard
  useTimerCardTour({ isDarkMode })

  const {
    status,
    timeRemaining,
    progress,
    currentSession,
    formattedTime,
    addedTime,
    startTimer,
    pauseTimer,
    resumeTimer,
    toggleMode,
    restartTimer,
  } = useTimer()

  // Determina texto do subtexto baseado no status
  const getSubtext = () => {
    if (status === 'idle') return 'Pronto para iniciar'
    if (status === 'running') return 'Em andamento'
    if (status === 'paused') return 'Pausado'
    if (status === 'stopped') return 'Sessão finalizada'
    return ''
  }

  // Handler para play/pause
  const handlePlayPause = () => {
    if (status === 'idle' || status === 'stopped') {
      startTimer()
    } else if (status === 'running') {
      pauseTimer()
    } else if (status === 'paused') {
      resumeTimer()
    }
  }

  const accentColor = currentSession?.color || '#027A00'
  const subject = currentSession?.subject

  // Formata tempo decorrido para exibir progresso
  const getTimeElapsed = () => {
    const totalSeconds = (currentSession?.duration || 0) + addedTime
    // Se finalizado, mostra tempo total
    if (isFinished) {
      const minutes = Math.floor(totalSeconds / 60)
      return `${minutes}min`
    }
    const elapsedSeconds = totalSeconds - timeRemaining
    const minutes = Math.floor(elapsedSeconds / 60)
    return `${minutes}min`
  }

  const getTotalTime = () => {
    const totalSeconds = currentSession?.duration || 0
    const minutes = Math.floor(totalSeconds / 60)
    return `${minutes}min`
  }

  // Formata tempo extra em minutos para exibição
  const getAddedTimeDisplay = () => {
    if (addedTime <= 0) return null
    const minutes = Math.floor(addedTime / 60)

    if (minutes > 60) {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `+${hours}h ${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`
    } else {
      return `+${minutes}min`
    }
  }

  const isFinished = timeRemaining <= 0 && status !== 'idle'
  const displayProgress = isFinished ? 100 : progress

  // Layout minimalista para mobile
  if (isMobile) {
    return (
      <CardContainer ref={cardRef} accentColor={accentColor} data-tour="timer-card-container">
        <MobileLayoutContainer>
          <MobileButtonGroup>
            {/* Play/Pause */}
            {!isFinished && (
              <IconButton
                onClick={handlePlayPause}
                tooltip={
                  status === 'running' ? 'Pausar' : status === 'paused' ? 'Retomar' : 'Iniciar'
                }
                active={true}
                disabled={status === 'idle' && !currentSession}
                data-tour="timer-card-play-pause"
              >
                {status === 'running' ? <IoPauseSharp /> : <IoPlaySharp />}
              </IconButton>
            )}

            {/* Reiniciar quando finalizado */}
            {isFinished && (
              <IconButton active={true} onClick={restartTimer} tooltip="Reiniciar">
                <IoRefreshSharp />
              </IconButton>
            )}

            {/* Stop */}
            <IconButton
              onClick={onStopClick}
              active={true}
              tooltip={isFinished ? 'Finalizar ciclo' : 'Parar'}
              disabled={status === 'idle'}
              data-tour="timer-card-stop"
            >
              <IoStopSharp />
            </IconButton>
          </MobileButtonGroup>

          {/* Tempo */}
          <MobileTimeDisplay data-tour="timer-card-time-display">{formattedTime}</MobileTimeDisplay>

          {/* Full-Screen */}
          <IconButton onClick={toggleMode} tooltip="Tela cheia" data-tour="timer-card-expand">
            <IoExpandSharp />
          </IconButton>
        </MobileLayoutContainer>
      </CardContainer>
    )
  }

  // Layout desktop (original)
  return (
    <CardContainer ref={cardRef} accentColor={accentColor} data-tour="timer-card-container">
      <CardHeader>
        <DisciplineLabel data-tour="timer-card-discipline">{subject}</DisciplineLabel>
        <div style={{ display: 'flex', gap: '4px' }}>
          <IconButton onClick={toggleMode} tooltip="Tela cheia" data-tour="timer-card-expand">
            <IoExpandSharp />
          </IconButton>
          <IconButton
            onClick={onCloseClick}
            tooltip="Fechar cronômetro"
            data-tour="timer-card-close"
          >
            <IoClose />
          </IconButton>
        </div>
      </CardHeader>

      <CardBody>
        <TimeDisplay data-tour="timer-card-time-display">{formattedTime}</TimeDisplay>
        <TimeSubtext data-tour="timer-card-status">{getSubtext()}</TimeSubtext>

        {/* <ProgressSection>
          <ProgressBar data-tour="timer-card-progress-bar">
            <ProgressFill progress={displayProgress} />
          </ProgressBar>
          <ProgressValue data-tour="timer-card-progress-value">
            {getTimeElapsed()} / {getTotalTime()}
            {getAddedTimeDisplay() && <AddedTimeBadge>{getAddedTimeDisplay()}</AddedTimeBadge>}
          </ProgressValue>
        </ProgressSection> */}
      </CardBody>

      <CardFooter>
        <ButtonContainer>
          {/* Play/Pause - esconde quando finalizado */}
          {!isFinished && (
            <IconButton
              onClick={handlePlayPause}
              tooltip={
                status === 'running' ? 'Pausar' : status === 'paused' ? 'Retomar' : 'Iniciar'
              }
              active={true}
              disabled={status === 'idle' && !currentSession}
              data-tour="timer-card-play-pause"
            >
              {status === 'running' ? <IoPauseSharp /> : <IoPlaySharp />}
            </IconButton>
          )}

          {/* Reiniciar - sempre com texto quando finalizado */}
          {isFinished && (
            <IconButton active={true} onClick={restartTimer} tooltip="Reiniciar">
              <IoRefreshSharp />
            </IconButton>
          )}

          {/* Parar/Finalizar */}
          <IconButton
            onClick={onStopClick}
            active={true}
            tooltip={isFinished ? 'Finalizar ciclo' : 'Parar'}
            disabled={status === 'idle'}
            data-tour="timer-card-stop"
          >
            <IoStopSharp />
          </IconButton>
        </ButtonContainer>
      </CardFooter>
    </CardContainer>
  )
}

export default TimerCard
